import type AppStore from '@src/app/AppStore'

import OpenPhoneProtocolHandler, {
  PROTOCOL_OPENPHONE_DESKTOP,
  PROTOCOL_QUO_DESKTOP,
} from './CustomProtocolHandler'
import MMSProtocolHandler, { PROTOCOL as PROTOCOL_MMS } from './MMSProtocolHandler'
import type ProtocolHandler from './ProtocolHandler'
import SMSProtocolHandler, { PROTOCOL as PROTOCOL_SMS } from './SMSProtocolHandler'
import TelProtocolHandler, { PROTOCOL as PROTOCOL_TEL } from './TelProtocolHandler'
import WebOpenPhoneProtocolHandler, {
  PROTOCOL as PROTOCOL_OPENPHONE_WEB,
} from './WebOpenPhoneProtocolHandler'
import WebQuoProtocolHandler, {
  PROTOCOL as PROTOCOL_QUO_WEB,
} from './WebQuoProtocolHandler'

const PROTOCOLS = [
  PROTOCOL_MMS,
  PROTOCOL_SMS,
  PROTOCOL_TEL,
  PROTOCOL_OPENPHONE_WEB,
  PROTOCOL_OPENPHONE_DESKTOP,
  PROTOCOL_QUO_WEB,
  PROTOCOL_QUO_DESKTOP,
] as const

type Protocol = (typeof PROTOCOLS)[number]

const isProtocol = (protocol: string): protocol is Protocol =>
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- FIXME: Fix this ESLint violation!
  PROTOCOLS.includes(protocol as any)

/**
 * Routes incoming URLs to their respective handlers.
 */
export default class URLRouter {
  protected readonly handlers: { [P in Protocol]: ProtocolHandler }

  readonly protocols: ReadonlySet<Protocol> = new Set(PROTOCOLS)
  readonly webProtocols: ReadonlySet<Protocol>
  readonly desktopProtocols: ReadonlySet<Protocol>

  constructor(protected app: AppStore) {
    this.handlers = {
      [PROTOCOL_MMS]: new MMSProtocolHandler(this.app),
      [PROTOCOL_SMS]: new SMSProtocolHandler(this.app),
      [PROTOCOL_TEL]: new TelProtocolHandler(this.app),
      [PROTOCOL_OPENPHONE_WEB]: new WebOpenPhoneProtocolHandler(this.app),
      [PROTOCOL_OPENPHONE_DESKTOP]: new OpenPhoneProtocolHandler(
        this.app,
        PROTOCOL_OPENPHONE_DESKTOP,
      ),
      [PROTOCOL_QUO_WEB]: new WebQuoProtocolHandler(this.app),
      [PROTOCOL_QUO_DESKTOP]: new OpenPhoneProtocolHandler(
        this.app,
        PROTOCOL_QUO_DESKTOP,
      ),
    }

    this.webProtocols = new Set(
      PROTOCOLS.filter((protocol) => this.handlers[protocol].web),
    )

    this.desktopProtocols = new Set(
      PROTOCOLS.filter((protocol) => this.handlers[protocol].desktop),
    )
  }

  route(incomingUrl: URL | string): void {
    const url = typeof incomingUrl === 'string' ? new URL(incomingUrl) : incomingUrl

    if (!url) {
      return
    }

    const protocol = this.getRegisteredProtocol(url)

    if (protocol) {
      const handler = this.handlers[protocol]
      handler.handle(url)
      this.app.focus()
    }
  }

  protected getRegisteredProtocol(url: URL): Protocol | null {
    // trim off the colon in the url protocol
    const protocol = url.protocol.substring(0, url.protocol.length - 1)

    if (!isProtocol(protocol)) {
      return null
    }

    return protocol
  }
}
